import "./CommunicationSearch.scss";
import clsx from "clsx";
import { ChangeEvent, Fragment, useCallback, useMemo, useState } from "react";
import { Button, Col } from "@/components";
import { Close, Spinner } from "@/icons";
import { Service } from "@anoki/fse-common";

export type Suggestion = {
  category: string;
  items: Service[];
};

export type CommunicationSearchProps = {
  btnText: string;
  helperText?: string;
  value: string;
  items: Suggestion[];
  loading?: boolean;
  emptyLabel?: string;
  setValue: (value: string) => void;
  setSelectedElement: (value: Service) => void;
};

export const CommunicationSearch = ({
  btnText,
  helperText = "Cosa vuoi approfondire?",
  value,
  loading,
  items,
  emptyLabel = "Nessun risultato disponibile",
  setValue,
  setSelectedElement,
}: CommunicationSearchProps) => {
  const [focus, setFocus] = useState(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
  };

  const handleSelectItem = (item: Service) => {
    setSelectedElement(item);
    setFocus(false);
  };

  const highlightMatch = useCallback((text: string, query: string) => {
    if (!query) return text;

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;

    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const afterMatch = text.slice(index + query.length);

    return (
      <>
        {beforeMatch}
        <span className={"text-primary fw-bold"}>{match}</span>
        {afterMatch}
      </>
    );
  }, []);

  const showMenu = useMemo(() => {
    if (focus) {
      return true;
    }

    if (value.length >= 3 && items?.length > 0) {
      return true;
    }

    return focus;
  }, [focus, items?.length, value.length]);

  return (
    <div
      className={clsx(
        "ui-communication-search",
        "br-x4 bg-border-secondary w-full",
      )}
      onBlur={() => {
        setTimeout(() => {
          setFocus(false);
        }, 150);
      }}
    >
      <div className={"position-relative w-full"}>
        <input
          id="search"
          type="search"
          className={clsx(
            "ui-communication-search-input",
            "fs-6 md-fs-custom text-slate-20 br-x4 w-full pl-x24",
          )}
          placeholder={helperText}
          aria-label="Barra di ricerca"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setFocus(true)}
        />
        {loading && <Spinner className="ui-loading-spinner" />}
        {value && value.length >= 3 && (
          <Close
            className={"ui-clean-button"}
            onClick={() => {
              handleInputChange({ target: { value: "" } } as any);
            }}
          />
        )}
      </div>

      <div
        className={clsx("ui-wrap-list-item-search shadow position-absolute", {
          "py-x24": showMenu,
        })}
      >
        <Col
          gap="x24"
          fw="regular"
          text="secondary"
          fs="6"
          className="ui-list-item-search overflow-y-auto"
        >
          {showMenu && (
            <>
              {items.map((group, index) =>
                group.items?.length ? (
                  <Fragment key={index}>
                    <div>
                      <h1 className="category-title fw-semibold text-dark fs-6 mb-x8 px-x24">
                        {group.category}
                      </h1>
                      <ul className="list-unstyled">
                        {group.items.map((item) => (
                          <li
                            key={item.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();

                              handleSelectItem(item);
                            }}
                            className="cursor-pointer py-x8 px-x24"
                          >
                            {highlightMatch(item.name, value || "")}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Fragment>
                ) : (
                  <></>
                ),
              )}

              {!loading &&
              items.every((suggestion) => suggestion.items.length === 0) ? (
                <p className=" px-x24 fs-6">{emptyLabel}</p>
              ) : (
                ""
              )}
            </>
          )}
        </Col>
      </div>

      <div className={clsx("ui-div-btn")}>
        <Button
          className={clsx(
            "ui-btn-search",
            "bg-primary text-white fs-7 md-fs-6 fw-semibold br-x4",
          )}
          onClick={() => {
            handleInputChange({ target: { value } } as any);
            setFocus(true);
          }}
        >
          {btnText}
        </Button>
      </div>
    </div>
  );
};
